// #74a — read-your-writes gate hints.
//
// A live reference to the current run's recipient-scoped feedback log: the
// SHARED array app.ts maintains (KV-fetched ∪ just-submitted, deduped by
// event_id, retractions spliced out). forms.ts reads it at submit so a gated
// close / move / placement carries the recipient's just-POSTed events to the
// Worker gate, closing the Cloudflare-KV read-your-writes window that let an
// emptied sub-category report "one item still open" even though the move-out
// had already been submitted (the #74a live blocker the operator hit on the
// 6/16 call). The Worker unions these with its own KV read (deduped by
// event_id) before computing the gate — see worker clientGateEventSchema.
//
// A module-level *reference*, not a copy: app.ts mutates the array in place
// (push on feedback-submitted, splice on feedback-retracted), so reads here
// always reflect the latest. Registered per run; cleared on nav-away so a
// stale run's events never leak into another run's gate.

import type { SubmittedFeedbackEvent } from "./briefings";

let ref: { runId: string; events: SubmittedFeedbackEvent[] } | null = null;

export function registerSessionEvents(
  runId: string,
  events: SubmittedFeedbackEvent[],
): void {
  ref = { runId, events };
}

export function clearSessionEvents(): void {
  ref = null;
}

/** The transport shape the Worker's clientGateEventSchema accepts. */
export interface GateHintEvent {
  event_id: string;
  kind: string;
  target?: { id: string };
  payload: Record<string, unknown>;
  submitted_at: string;
}

/** #74a: the run's known in-session events as Worker gate hints. Empty unless
 *  the registered run matches the one being submitted against (a stale ref from
 *  a previously-viewed run never leaks). */
export function gateHintEvents(runId: string): GateHintEvent[] {
  if (!ref || ref.runId !== runId) return [];
  return ref.events.map((e) => ({
    event_id: e.event_id,
    kind: e.kind,
    target: e.target ? { id: e.target.id } : undefined,
    payload: e.payload ?? {},
    submitted_at: e.submitted_at,
  }));
}
